import { useCallback, useMemo, useState } from "react";
import { Flex, Button, Text, Box } from "@radix-ui/themes";
import { useQuery, useMutation } from "react-query";
import { toast } from "sonner";

import { Dialog } from "@/components/shared/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";

interface AddToRegressionSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  releaseConversationId: number;
  simulatorId: number;
}

export function AddToRegressionSetDialog({
  open,
  onOpenChange,
  releaseConversationId,
  simulatorId,
}: AddToRegressionSetDialogProps) {
  const regressionApi = useRegressionApi();
  const [selectedSetId, setSelectedSetId] = useState<string>("");

  const { data: regressionSets, isLoading: isSetsLoading } = useQuery(
    ["regression-sets", simulatorId.toString()],
    () => regressionApi.getRegressionSetsBySimulatorId(simulatorId.toString()),
    { enabled: open }
  );

  const { mutate: addToSet, isLoading: isAdding } = useMutation(
    () =>
      regressionApi.createRegressionSetItem(selectedSetId, {
        releaseConversationId,
      }),
    {
      onSuccess: () => {
        const setName =
          regressionSets?.find((s) => s.id === selectedSetId)?.name ??
          "regression set";
        toast.success(`Session added to "${setName}"`);
        handleClose();
      },
      onError: () => {
        toast.error("Failed to add session to regression set");
      },
    }
  );

  const handleClose = useCallback(() => {
    setSelectedSetId("");
    onOpenChange(false);
  }, [onOpenChange]);

  const handleAdd = useCallback(() => {
    if (!selectedSetId) {
      toast.error("Please select a regression set");
      return;
    }
    addToSet();
  }, [selectedSetId, addToSet]);

  const handleSetChange = useCallback((value: string) => {
    setSelectedSetId(value);
  }, []);

  const setOptions = useMemo(() => {
    return regressionSets ?? [];
  }, [regressionSets]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Flex direction="column" gap="4" p="4">
          <Text size="5" weight="medium">
            Add to Regression Set
          </Text>

          <Text size="2" color="gray">
            Add this training session to an existing regression test set.
          </Text>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Regression Set
            </Text>
            <Select value={selectedSetId} onValueChange={handleSetChange}>
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isSetsLoading ? "Loading sets..." : "Select a regression set"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {setOptions.map((set) => (
                  <SelectItem key={set.id} value={set.id}>
                    {set.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Box>

          <Flex gap="3" mt="2" justify="end">
            <Button variant="soft" color="gray" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleAdd}
              disabled={isAdding || !selectedSetId || isSetsLoading}
            >
              {isAdding ? "Adding..." : "Add to Set"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
